import { useRef, useState } from 'react';

import {
  MARKDOWN_TOOLS,
  applyTool,
  parseMarkdown,
  wordCount,
  type Block,
  type Inline,
} from '@/lib/markdown';

/**
 * The Markdown box both editors use: a toolbar, a textarea, and a preview.
 *
 * The preview renders React from parsed spans rather than an HTML string. The
 * design's own version builds HTML and hands it to `dangerouslySetInnerHTML`;
 * text somebody typed then depends on an escaping step, and the studio's posts
 * are public. Data in, elements out, and there is nothing left to escape.
 */

const Spans = ({ spans }: { spans: Inline[] }) => (
  <>
    {spans.map((span, at) => {
      const key = `${span.kind}-${at}`;
      if (span.kind === 'bold') return <strong key={key}>{span.text}</strong>;
      if (span.kind === 'italic') return <em key={key}>{span.text}</em>;
      if (span.kind === 'code')
        return (
          <code
            key={key}
            className="rounded px-1 py-px font-mono text-[11.5px]"
            style={{
              background: 'var(--panel2)',
              color: 'var(--text)',
              border: '1px solid var(--line)',
            }}
          >
            {span.text}
          </code>
        );
      if (span.kind === 'link')
        return (
          /*
            ⚠️ `.prose-link` rather than the blanket `a:hover` rule this
            replaces: a link sitting inside a paragraph has to be findable as a
            link, and a link that *is* the whole control — a rail item, a tab, a
            table row — does not. The underline is at rest rather than on hover,
            for the contrast numbers written beside the class.
          */
          <a
            key={key}
            href={span.href}
            rel="noreferrer noopener"
            target="_blank"
            className="prose-link focus-ring"
          >
            {span.text}
          </a>
        );
      return <span key={key}>{span.text}</span>;
    })}
  </>
);

const Rendered = ({ blocks }: { blocks: Block[] }) => (
  <>
    {blocks.map((block, at) => {
      const key = `${block.kind}-${at}`;
      switch (block.kind) {
        case 'heading':
          return (
            <p
              key={key}
              className="m-0 mb-2 font-semibold tracking-[-0.015em]"
              style={{ fontSize: block.level <= 2 ? 17 : 15 }}
              role="heading"
              aria-level={block.level}
            >
              <Spans spans={block.spans} />
            </p>
          );
        case 'quote':
          return (
            <blockquote
              key={key}
              className="m-0 mb-3 border-l-2 pl-3"
              style={{ borderColor: 'var(--accent)', color: 'var(--muted)' }}
            >
              <Spans spans={block.spans} />
            </blockquote>
          );
        case 'bullets':
          return (
            <ul key={key} className="m-0 mb-3 flex list-disc flex-col gap-1 pl-5">
              {block.items.map((item, index) => (
                <li key={index}>
                  <Spans spans={item} />
                </li>
              ))}
            </ul>
          );
        case 'numbers':
          return (
            <ol key={key} className="m-0 mb-3 flex list-decimal flex-col gap-1 pl-5">
              {block.items.map((item, index) => (
                <li key={index}>
                  <Spans spans={item} />
                </li>
              ))}
            </ol>
          );
        case 'code':
          return (
            <pre
              key={key}
              className="m-0 mb-3 overflow-x-auto rounded-lg p-2.5 font-mono text-[11.5px]"
              style={{ background: 'var(--panel2)', border: '1px solid var(--line)' }}
            >
              {block.text}
            </pre>
          );
        case 'table':
          return (
            <div key={key} className="mb-3 overflow-x-auto">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr>
                    {block.header.map((cell, column) => (
                      <th
                        key={column}
                        scope="col"
                        className="border-b px-2 py-1 font-semibold"
                        style={{ borderColor: 'var(--line)', textAlign: block.align[column] ?? 'left' }}
                      >
                        <Spans spans={cell} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, index) => (
                    <tr key={index}>
                      {row.map((cell, column) => (
                        <td
                          key={column}
                          className="border-b px-2 py-1"
                          style={{ borderColor: 'var(--line)', textAlign: block.align[column] ?? 'left' }}
                        >
                          <Spans spans={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        default:
          return (
            <p key={key} className="m-0 mb-3">
              <Spans spans={block.spans} />
            </p>
          );
      }
    })}
  </>
);

export function MarkdownField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  meta,
  heading = false,
  rows = 16,
  maxLength,
  mono = false,
}: {
  /** Names the textarea, and the panel heading beside the word count. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** The line under the box. Omitted where the design shows nothing there. */
  hint?: string;
  /**
   * What to print beside the label instead of the word count.
   *
   * A word count is the right figure for a description or a post. For the
   * studio's legal documents the design counts sections and characters
   * instead, because what somebody checks there is whether every clause still
   * has its heading.
   */
  meta?: string;
  /**
   * Set the label as a card's heading rather than as a control's name.
   *
   * The design draws this box three times and names it two different ways. On
   * the post editor the Markdown box *is* the card, so "Body" is the card's
   * heading — 13px. On the app editor and in Settings it sits among other
   * fields, so "Description" and "Document · one heading per section" are
   * labels like the ones above them — 11.5px. The two were the same size until
   * the design made card headings louder, which is why one flag now says which.
   */
  heading?: boolean;
  rows?: number;
  maxLength?: number;
  /**
   * Set the writing surface in the mono face.
   *
   * The design does this for the studio's legal documents and not for an app's
   * description, and the difference is real: a legal document is structured
   * text where the `##` in front of a clause is part of what you are checking,
   * while a description is prose that happens to be written in Markdown.
   */
  mono?: boolean;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  const run = (index: number) => {
    const element = field.current;
    if (!element) return;
    const edit = applyTool(value, element.selectionStart, element.selectionEnd, MARKDOWN_TOOLS[index]);
    onChange(maxLength ? edit.text.slice(0, maxLength) : edit.text);
    /*
     * The cursor is restored after React has written the new value, not before
     * — setting it now puts it back where the old text ended and the next
     * keystroke lands in the wrong place.
     */
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(edit.start, edit.end);
    });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <span className={heading ? 'eyebrow' : 'field-label'}>{label}</span>
        <span className="font-mono text-[9.5px]" style={{ color: 'var(--muted)' }}>
          {meta ?? `${wordCount(value)} words`}
        </span>
        <span
          className="ml-auto inline-flex rounded-lg border p-0.5"
          style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
        >
          {[
            ['Write', false],
            ['Preview', true],
          ].map(([name, on]) => (
            <button
              key={name as string}
              type="button"
              onClick={() => setPreview(on as boolean)}
              aria-pressed={preview === on}
              className="seg focus-ring min-h-[26px] cursor-pointer px-2.5 py-[5px] text-[11.5px]"
            >
              {name as string}
            </button>
          ))}
        </span>
      </span>

      <div
        className="overflow-hidden rounded-[10px] border"
        style={{ borderColor: 'var(--line)', background: 'var(--in)' }}
      >
        <div
          className="flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1"
          style={{ borderColor: 'var(--line)', background: 'var(--panel2)' }}
        >
          {MARKDOWN_TOOLS.map((tool, index) => (
            <button
              key={tool.name}
              type="button"
              aria-label={tool.name}
              title={tool.name}
              disabled={preview}
              onClick={() => run(index)}
              className="btn-quiet focus-ring flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-[5px] px-1.5 font-mono text-[11px] disabled:cursor-default disabled:opacity-40"
              style={{ color: 'var(--dim)' }}
            >
              {tool.icon}
            </button>
          ))}
          <span
            className="ml-auto pr-1 font-mono text-[10px] tracking-normal"
            style={{ color: 'var(--dim)' }}
          >
            markdown
          </span>
        </div>

        {preview ? (
          <div
            className="max-h-[520px] min-h-[280px] overflow-auto px-4 py-3.5 text-[13.5px] leading-[1.75]"
            style={{ color: 'var(--text)' }}
          >
            {value.trim() ? (
              <Rendered blocks={parseMarkdown(value)} />
            ) : (
              <span style={{ color: 'var(--dim)' }}>Nothing written yet.</span>
            )}
          </div>
        ) : (
          <textarea
            ref={field}
            aria-label={label}
            spellCheck={false}
            rows={rows}
            value={value}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            className={`block w-full min-h-[280px] resize-y border-0 bg-transparent outline-none ${
              mono
                ? 'px-3 py-2.5 font-mono text-[11.5px] leading-[1.75]'
                : 'px-4 py-3.5 text-[13.5px] leading-[1.75]'
            }`}
            style={{ color: 'var(--text)' }}
          />
        )}
      </div>
      {hint ? (
        <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
